import React from "react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { UserDetails } from "src/models/User";
import { SaasTherapistService } from "src/services/SaasTherapistService";
import Spinner from "../../../common/spinner/spinner";
import { Button } from "reactstrap";

interface PersonalInfoProps {
  clientId: string;
}

const PersonalInfo: React.FC<PersonalInfoProps> = ({ clientId }) => {
  const [clientPersonalDetails, setClientPersonalDetails] = useState<UserDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<UserDetails | null>(null);
  useEffect(() => {
    const fetchClientData = async () => {
      if (!clientId) return;

      try {
        setIsLoading(true);
        const data = await fetchClientDetails(clientId); // Await the function correctly
        setClientPersonalDetails(data);
        setEditFormData(data); // Initialize edit form data
        setIsLoading(false);
      } catch (error) {
        toast.error("Failed to fetch client details.", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
        setIsLoading(false);
      }
    };

    fetchClientData();
  }, [clientId]); // Dependency ensures the effect re-runs when clientId changes

  const fetchClientDetails = async (clientId: string) => {
    try {
      const res = await SaasTherapistService.getPersonalDetails(clientId);
      if (res.success) {
        return res.data; // Return the data
      } else {
        toast.error("Error loading data", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
        return null; // Return null to handle the error
      }
    } catch (error) {
      toast.error("Error fetching client details.", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
      return null; // Return null in case of failure
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditFormData({ ...clientPersonalDetails });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({ ...clientPersonalDetails });
  };

  const handleSaveEdit = async () => {
    if (!editFormData || !clientId) return;

    try {
      setIsLoading(true);

      // Only send fields that have data (partial update)
      const allowedFields = [
        'firstname',
        'lastname',
        'dateOfBirth',
        'gender',
        'primaryPhone',
        'streetAddress',
        'city',
        'state',
        'zipCode',
        'homePhone'
      ];

      const updateData: Partial<UserDetails> = {};
      allowedFields.forEach(field => {
        const value = editFormData[field as keyof UserDetails];
        if (value !== undefined && value !== null && value !== '') {
          updateData[field as keyof UserDetails] = value;
        }
      });

      const res = await SaasTherapistService.updatePersonalDetails(clientId, updateData);

      if (res.success) {
        setClientPersonalDetails(editFormData);
        setIsEditing(false);
        toast.success("Personal information updated successfully!", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      } else {
        toast.error(res.error || "Failed to update personal information.", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      }
      setIsLoading(false);
    } catch (error) {
      toast.error("Error updating personal information.", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof UserDetails, value: string) => {
    if (!editFormData) return;
    setEditFormData({
      ...editFormData,
      [field]: value
    });
  };

  if (!clientPersonalDetails) {
    return <p>Loading client data...</p>;
  }

  // This function extracts yyyy-mm-dd from an ISO string without timezone processing
  const getDateWithoutTimezone = (dateInput: string | Date | undefined | null): string => {
    if (!dateInput) return "";

    // Convert to string first
    const dateString = dateInput.toString();

    // Extract the yyyy-mm-dd part
    const datePart = dateString.substring(0, 10);
    if (!datePart || datePart.length < 10) return "";

    return datePart;
  };

  const formatDateToString = (date: Date | string | undefined): string => {
    if (!date) return ""; // Handle undefined dates

    // If it's already a string, extract the date part without timezone conversion
    if (typeof date === 'string') {
      const datePart = date.substring(0, 10);
      if (datePart && datePart.length === 10) {
        const [year, month, day] = datePart.split('-');
        return `${month}/${day}/${year}`;
      }
    }

    // For Date objects, use the same approach
    const dateStr = date.toString();
    const datePart = dateStr.substring(0, 10);
    if (datePart && datePart.length === 10) {
      const [year, month, day] = datePart.split('-');
      return `${month}/${day}/${year}`;
    }

    return "";
  };
  return (
    <div style={{ maxWidth: "100%", padding: "10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h3 style={{ color: "#f27e0c", marginTop: "3px", marginBottom: "0" }}>Personal Information</h3>
        <div>
          {!isEditing ? (
            <Button
              color="primary"
              size="sm"
              onClick={handleEditClick}
              style={{ fontSize: "12px", padding: "6px 12px" }}
            >
              <i className="fa fa-edit me-1"></i>
              Edit
            </Button>
          ) : (
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                color="success"
                size="sm"
                onClick={handleSaveEdit}
                disabled={isLoading}
                style={{ fontSize: "12px", padding: "6px 12px" }}
              >
                <i className="fa fa-save me-1"></i>
                Save
              </Button>
              <Button
                color="secondary"
                size="sm"
                onClick={handleCancelEdit}
                disabled={isLoading}
                style={{ fontSize: "12px", padding: "6px 12px" }}
              >
                <i className="fa fa-times me-1"></i>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className={"d-flex justify-content-center align-items-center"} style={{minHeight:"25vh",width:"100%",position:"relative"}}>
          <Spinner className={"bouncer"} />
        </div>
      ) : (
        <form style={{ fontSize: "12.5px" }}>
          <div style={{ marginBottom: "15px", display: "flex", gap: "15px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: "5px", marginTop: "20px", fontWeight: "bold" }}>First Name</label>
              <input
                type="text"
                value={isEditing ? (editFormData?.firstname || "") : (clientPersonalDetails?.firstname || "")}
                onChange={(e) => isEditing && handleInputChange('firstname', e.target.value)}
                readOnly={!isEditing}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: isEditing ? "#fff" : "#f9fafb",
                  cursor: isEditing ? "text" : "default"
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: "5px", marginTop: "20px", fontWeight: "bold" }}>Last Name</label>
              <input
                type="text"
                value={isEditing ? (editFormData?.lastname || "") : (clientPersonalDetails?.lastname || "")}
                onChange={(e) => isEditing && handleInputChange('lastname', e.target.value)}
                readOnly={!isEditing}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: isEditing ? "#fff" : "#f9fafb",
                  cursor: isEditing ? "text" : "default"
                }}
              />
            </div>
          </div>
          <div style={{ marginBottom: "15px", display: "flex", gap: "15px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Birthday</label>
              <input
                type={isEditing ? "date" : "text"}
                value={isEditing ?
                  getDateWithoutTimezone(editFormData?.dateOfBirth) :
                  formatDateToString(clientPersonalDetails?.dateOfBirth)
                }
                onChange={(e) => isEditing && handleInputChange('dateOfBirth', e.target.value)}
                readOnly={!isEditing}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: isEditing ? "#fff" : "#f9fafb",
                  cursor: isEditing ? "text" : "default"
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Gender</label>
              {isEditing ? (
                <select
                  value={editFormData?.gender || ""}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              ) : (
                <input
                  type="text"
                  value={clientPersonalDetails?.gender || ""}
                  readOnly
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#f9fafb",
                    cursor: "default"
                  }}
                />
              )}
            </div>
          </div>
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Contact Number</label>
            <input
              type="text"
              value={isEditing ? (editFormData?.primaryPhone || "") : (clientPersonalDetails?.primaryPhone || "")}
              onChange={(e) => isEditing && handleInputChange('primaryPhone', e.target.value)}
              readOnly={!isEditing}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: isEditing ? "#fff" : "#f9fafb",
                cursor: isEditing ? "text" : "default"
              }}
            />
          </div>

          {isEditing ? (
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "10px", fontWeight: "bold" }}>Address</label>
              <div style={{ display: "flex", gap: "15px", marginBottom: "15px" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "normal", fontSize: "11px" }}>Street Address</label>
                  <input
                    type="text"
                    value={editFormData?.streetAddress || ""}
                    onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
              </div>
              <div style={{ display: "flex", gap: "15px" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "normal", fontSize: "11px" }}>City</label>
                  <input
                    type="text"
                    value={editFormData?.city || ""}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
                <div style={{ flex: 0.7 }}>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "normal", fontSize: "11px" }}>State</label>
                  <input
                    type="text"
                    value={editFormData?.state || ""}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
                <div style={{ flex: 0.7 }}>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "normal", fontSize: "11px" }}>Zip Code</label>
                  <input
                    type="text"
                    value={editFormData?.zipCode || ""}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Address</label>
              <input
                type="text"
                value={`${clientPersonalDetails?.streetAddress || ''}, ${clientPersonalDetails?.city || ''}, ${clientPersonalDetails?.state || ''} ${clientPersonalDetails?.zipCode || ''}`.replace(/, ,| ,/g, '').trim()}
                readOnly
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: "#f9fafb",
                  cursor: "default"
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Emergency Contact</label>
            <input
              type="text"
              value={isEditing ? (editFormData?.homePhone || "") : (clientPersonalDetails?.homePhone || "")}
              onChange={(e) => isEditing && handleInputChange('homePhone', e.target.value)}
              readOnly={!isEditing}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: isEditing ? "#fff" : "#f9fafb",
                cursor: isEditing ? "text" : "default"
              }}
            />
          </div>
        </form>
      )}

    </div>
  );
};

export default PersonalInfo;
